import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, BoxProps, Button, IconButton, InputAdornment, SxProps, TextField, Typography, lighten, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { UseFieldConfig, useField } from "react-final-form";
import { useIntl } from "react-intl";

interface DropZoneProps extends BoxProps {
  dragging: boolean;
}
export function returnFileSize(number: number) {
  if (number < 1024) {
    return number + "bytes";
  } else if (number >= 1024 && number < 1048576) {
    return (number / 1024).toFixed(1) + "KB";
  } else if (number >= 1048576) {
    return (number / 1048576).toFixed(1) + "MB";
  }
}

const convertFileToBase64 = (file: File, callback: (result: string) => void) => {
  const reader = new FileReader();
  reader.onload = () => {
    callback(reader.result as string);
  };
  reader.readAsDataURL(file);
};

const DropZone = styled(Box, {
  shouldForwardProp: (propName) => propName !== "dragging",
})<DropZoneProps>(({ theme, dragging }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1),
  borderWidth: 2,
  borderRadius: 2,
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  outline: "none",
  transition: "border .24s ease-in-out",
  borderColor: dragging ? `${theme.palette.primary.main}` : "#eeeeee",
}));

const B64FileField = ({
  name,
  accept,
  label,
  disabled,
  sx,
  highlightDirty = true,
  onChange,
  ...fieldConfig
}: {
  name: string;
  previewName?: string;
  renderPreview?: (src: string, file?: File) => React.ReactNode;
  accept?: string;
  label?: string;
  disabled?: boolean;
  sx?: SxProps;
  highlightDirty?: boolean;
  onChange?: (value: string | null) => void;
} & Pick<UseFieldConfig<string | null>, "validate">) => {
  const { formatMessage } = useIntl();
  const [dragging, setDragging] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");

  const acceptedFileFormat = accept?.split("/");

  const {
    input: { value, onChange: onChangeRawData, ...input },
    meta: { dirty: rawDataDirty },
  } = useField<string | null>(name, {
    allowNull: true,
    afterSubmit: () => {
      onChangeRawData(null);
    },

    ...fieldConfig,
  });

  const handleFileSelected = (file: File) => {
    setSelectedFileName(file.name);
    convertFileToBase64(file, (fileDataURL) => {
      onChangeRawData(fileDataURL.split(",")[1]);
    });
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelected(droppedFile);
    }
    setDragging(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleFileSelected(selectedFile);
    }
    event.currentTarget.value = "";
  };

  const clearFile = () => {
    onChangeRawData("");
    setSelectedFileName("");
  };

  useEffect(() => {
    if (!value) {
      setSelectedFileName("");
    }
    if (onChange) {
      onChange(value);
    }
  }, [onChange, value]);

  return (
    <>
      <DropZone
        dragging={dragging}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={[
          rawDataDirty &&
            highlightDirty && {
              backgroundColor: (theme) => lighten(theme.palette.warning.light, 0.6),
            },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Typography
          sx={{
            alignSelf: "start",
            position: "relative",
            top: "5px",
            left: "5px",
            padding: "0",
          }}
          variant="caption"
          color="primary"
          padding={1}
        >
          {acceptedFileFormat && (
            <>
              {label}: ({acceptedFileFormat[1]})
            </>
          )}
        </Typography>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row",
          }}
        >
          <>
            <CloudUploadIcon sx={{ height: 32, width: 32 }}></CloudUploadIcon>
            <Typography variant="body2" align="center" sx={{ ml: 2 }}>
              {formatMessage({
                defaultMessage: "Drag the file here",
              })}
            </Typography>
          </>
        </div>

        {selectedFileName ? (
          <TextField
            value={selectedFileName}
            variant="outlined"
            size="small"
            hiddenLabel
            sx={{ m: 0, mt: 1, "& input": { pt: 0.625, pb: 0.625 }, ".MuiInputBase-root": { backgroundColor: "transparent" } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" disableTypography>
                  <IconButton size="small" onClick={clearFile}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              readOnly: true,
            }}
          />
        ) : (
          <Button
            variant="outlined"
            component="label"
            tabIndex={-1}
            sx={{
              mt: 1,
              "& input": {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                clipPath: "inset(50%)",
              },
            }}
          >
            <input {...input} accept={accept} disabled={disabled} type="file" onChange={handleFileChange} />
            <Typography variant="body2">
              {formatMessage({
                defaultMessage: "Choose a file to upload",
              })}
            </Typography>
          </Button>
        )}
      </DropZone>
    </>
  );
};

export default B64FileField;
